import React, { useState, useEffect } from 'react';
import { useNavigate, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from "@/lib/api";
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutDashboard, Building2, MessageSquare, Phone, LogOut, Menu, X, Pencil, Trash2, Plus, Upload, ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function AdminSidebar({ activeTab, setActiveTab, mobileOpen, setMobileOpen }) {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'spaces', label: 'Spaces', icon: Building2 },
    { id: 'enquiries', label: 'Enquiries', icon: MessageSquare },
    { id: 'contacts', label: 'Contacts', icon: Phone },
  ];

  return (
    <aside className={`fixed md:static inset-y-0 left-0 z-50 w-64 bg-[#0F172A] border-r border-white/10 transform transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}>
      <div className="p-6 border-b border-white/10">
        <h2 className="text-xl font-light text-[#F8FAFC] font-['Cormorant_Garamond']">Khan Complex</h2>
        <p className="text-xs text-[#94A3B8] font-['Outfit'] mt-1">Admin Panel</p>
      </div>
      <nav className="p-4 space-y-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`admin-nav-${tab.id}`}
            onClick={() => { setActiveTab(tab.id); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-['Outfit'] transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30'
                : 'text-[#94A3B8] hover:bg-white/5 hover:text-[#F8FAFC]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
        <button
          data-testid="admin-logout-btn"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm text-[#94A3B8] hover:bg-red-500/10 hover:text-red-400 transition-all font-['Outfit']"
        >
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </div>
    </aside>
  );
}

function DashboardView({ spaces, enquiries, contacts }) {
  const stats = [
    { label: 'Total Spaces', value: spaces.length, color: '#D4AF37' },
    { label: 'Available', value: spaces.filter(s => s.status === 'Available').length, color: '#22C55E' },
    { label: 'Enquiries', value: enquiries.length, color: '#3B82F6' },
    { label: 'Messages', value: contacts.length, color: '#A855F7' },
  ];

  return (
    <div data-testid="admin-dashboard-view">
      <h2 className="text-3xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] mb-8">Dashboard</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <div key={i} className="bg-[#0F172A] border border-white/5 rounded-lg p-6">
            <p className="text-xs tracking-[0.2em] uppercase text-[#94A3B8] font-bold mb-2 font-['Outfit']">{s.label}</p>
            <p className="text-3xl font-light font-['Cormorant_Garamond']" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-[#0F172A] border border-white/5 rounded-lg p-6">
        <h3 className="text-xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] mb-4">Recent Enquiries</h3>
        {enquiries.slice(0, 5).map((e, i) => (
          <div key={i} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
            <div>
              <p className="text-[#F8FAFC] text-sm font-['Outfit']">{e.name}</p>
              <p className="text-[#94A3B8] text-xs font-['Outfit']">{e.space_name || 'General'}</p>
            </div>
            <Badge className={`text-xs ${e.status === 'New' ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}`}>
              {e.status}
            </Badge>
          </div>
        ))}
        {enquiries.length === 0 && <p className="text-[#94A3B8] text-sm font-['Outfit']">No enquiries yet</p>}
      </div>
    </div>
  );
}

function SpacesView({ spaces, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSpace, setEditSpace] = useState(null);
  const [form, setForm] = useState({ name: '', floor: '1st Floor', space_type: 'Retail Shop', size: '120 sq ft', status: 'Available', price: '', description: '' });
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageSpace, setImageSpace] = useState(null);
  const [uploading, setUploading] = useState(false);

  const openAdd = () => {
    setEditSpace(null);
    setForm({ name: '', floor: '1st Floor', space_type: 'Retail Shop', size: '120 sq ft', status: 'Available', price: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (space) => {
    setEditSpace(space);
    setForm({ name: space.name, floor: space.floor, space_type: space.space_type, size: space.size, status: space.status, price: space.price || '', description: space.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
     if (editSpace) {
  await api.put(`/spaces/${editSpace.id}`, form);
  toast.success('Space updated');
} else {
  await api.post(`/spaces`, form);
  toast.success('Space added');
}
      setDialogOpen(false);
      onRefresh();
    } catch (err) {
      toast.error('Failed to save');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this space?')) return;
    try {
      await api.delete(`/spaces/${id}`);
      toast.success('Space deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const openImageUpload = (space) => {
    setImageSpace(space);
    setImageDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('files', f));
      const { data } = await api.post(`/upload/space-images`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
      const existingImages = imageSpace?.images || [];
      const newImages = [...existingImages, ...data.urls];
      await api.put(`/spaces/${imageSpace.id}/images`, { images: newImages });
      toast.success(`${data.urls.length} image(s) uploaded`);
      onRefresh();
      setImageDialogOpen(false);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const removeImage = async (spaceId, imgUrl, allImages) => {
    try {
      const updated = allImages.filter(u => u !== imgUrl);
      await api.put(`/spaces/${spaceId}/images`, { images: updated });
      toast.success('Image removed');
      onRefresh();
      setImageSpace(prev => prev ? { ...prev, images: updated } : null);
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div data-testid="admin-spaces-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-light text-[#F8FAFC] font-['Cormorant_Garamond']">Manage Spaces</h2>
        <Button data-testid="add-space-btn" onClick={openAdd} className="bg-[#D4AF37] text-[#020617] hover:bg-[#FCE69C]">
          <Plus className="w-4 h-4 mr-2" /> Add Space
        </Button>
      </div>
      <div className="bg-[#0F172A] border border-white/5 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-[#94A3B8]">Name</TableHead>
              <TableHead className="text-[#94A3B8]">Floor</TableHead>
              <TableHead className="text-[#94A3B8]">Type</TableHead>
              <TableHead className="text-[#94A3B8]">Size</TableHead>
              <TableHead className="text-[#94A3B8]">Status</TableHead>
              <TableHead className="text-[#94A3B8]">Images</TableHead>
              <TableHead className="text-[#94A3B8]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {spaces.map(space => (
              <TableRow key={space.id} className="border-white/5 hover:bg-white/5">
                <TableCell className="text-[#F8FAFC] font-['Outfit']">{space.name}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit']">{space.floor}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit']">{space.space_type}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit']">{space.size}</TableCell>
                <TableCell>
                  <Badge className={space.status === 'Available' ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30' : 'bg-[#94A3B8]/20 text-[#94A3B8] border-[#94A3B8]/30'}>
                    {space.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <button data-testid={`upload-images-${space.id}`} onClick={() => openImageUpload(space)} className="flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition-colors font-['Outfit']">
                    <ImageIcon className="w-3.5 h-3.5" /> {(space.images || []).length}
                  </button>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button data-testid={`edit-space-${space.id}`} onClick={() => openEdit(space)} className="p-1.5 rounded hover:bg-white/10 text-[#94A3B8] hover:text-[#D4AF37] transition-colors"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-space-${space.id}`} onClick={() => handleDelete(space.id)} className="p-1.5 rounded hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-[#F8FAFC] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light">Images - {imageSpace?.name}</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">Upload and manage space photos</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {imageSpace?.images && imageSpace.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {imageSpace.images.map((img, idx) => (
                  <div key={idx} className="relative group rounded overflow-hidden">
                    <img src={`${API}${img}`} alt={`Space ${idx}`} className="w-full h-24 object-cover" />
                    <button onClick={() => removeImage(imageSpace.id, img, imageSpace.images)} className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label data-testid="image-upload-input" className="block cursor-pointer">
              <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-[#D4AF37]/50 transition-colors">
                <Upload className="w-8 h-8 text-[#94A3B8] mx-auto mb-3" />
                <p className="text-sm text-[#94A3B8] font-['Outfit']">{uploading ? 'Uploading...' : 'Click to upload images'}</p>
                <p className="text-xs text-[#64748B] font-['Outfit'] mt-1">JPG, PNG, WebP supported</p>
              </div>
              <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={handleImageUpload} className="hidden" disabled={uploading} />
            </label>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-[#F8FAFC] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light">{editSpace ? 'Edit Space' : 'Add New Space'}</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">Fill in the space details below</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">Name</Label>
              <Input data-testid="space-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">Floor</Label>
                <Select value={form.floor} onValueChange={v => setForm({...form, floor: v})}>
                  <SelectTrigger data-testid="space-floor-select" className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    <SelectItem value="Ground Floor">Ground Floor</SelectItem>
                    <SelectItem value="1st Floor">1st Floor</SelectItem>
                    <SelectItem value="2nd Floor">2nd Floor</SelectItem>
                    <SelectItem value="3rd Floor">3rd Floor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">Type</Label>
                <Select value={form.space_type} onValueChange={v => setForm({...form, space_type: v})}>
                  <SelectTrigger data-testid="space-type-select" className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    <SelectItem value="Retail Shop">Retail Shop</SelectItem>
                    <SelectItem value="Office Space">Office Space</SelectItem>
                    <SelectItem value="Commercial Hall">Commercial Hall</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">Size</Label>
                <Input data-testid="space-size-input" value={form.size} onChange={e => setForm({...form, size: e.target.value})} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger data-testid="space-status-select" className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10">
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Occupied">Occupied</SelectItem>
                    <SelectItem value="Reserved">Reserved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-xs uppercase tracking-wider font-bold">Description</Label>
              <Textarea data-testid="space-description-input" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-[#1E293B] border-white/10 text-[#F8FAFC] mt-1" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="space-save-btn" onClick={handleSave} className="bg-[#D4AF37] text-[#020617] hover:bg-[#FCE69C]">
              {editSpace ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EnquiriesView({ enquiries, onRefresh }) {
  const updateStatus = async (id, status) => {
    try {
      await api.put(`/enquiries/${id}/status`, { status });
      toast.success('Status updated');
      onRefresh();
    } catch { toast.error('Failed to update'); }  
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm('Delete this enquiry?')) return;
    try {
      await api.delete(`/enquiries/${id}`);
      toast.success('Deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid="admin-enquiries-view">
      <h2 className="text-3xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] mb-6">Enquiries</h2>
      <div className="bg-[#0F172A] border border-white/5 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-[#94A3B8]">Name</TableHead>
              <TableHead className="text-[#94A3B8]">Phone</TableHead>
              <TableHead className="text-[#94A3B8]">Space</TableHead>
              <TableHead className="text-[#94A3B8]">Message</TableHead>
              <TableHead className="text-[#94A3B8]">Status</TableHead>
              <TableHead className="text-[#94A3B8]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enquiries.map(enq => (
              <TableRow key={enq.id} className="border-white/5 hover:bg-white/5">
                <TableCell className="text-[#F8FAFC] font-['Outfit']">{enq.name}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit']">{enq.phone}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit']">{enq.space_name || '-'}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit'] max-w-[200px] truncate">{enq.message || '-'}</TableCell>
                <TableCell>
                  <Badge className={enq.status === 'New' ? 'bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}>
                    {enq.status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    {enq.status === 'New' && (
                      <button data-testid={`review-enquiry-${enq.id}`} onClick={() => updateStatus(enq.id, 'Reviewed')} className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-colors font-['Outfit']">Mark Reviewed</button>
                    )}
                    <button data-testid={`delete-enquiry-${enq.id}`} onClick={() => deleteEnquiry(enq.id)} className="p-1.5 rounded hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {enquiries.length === 0 && <p className="text-center text-[#94A3B8] py-8 font-['Outfit']">No enquiries yet</p>}
      </div>
    </div>
  );
}

function ContactsView({ contacts, onRefresh }) {
  const deleteContact = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/contact/${id}`);
      toast.success('Deleted');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-contacts-view">
      <h2 className="text-3xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] mb-6">Contact Messages</h2>
      <div className="bg-[#0F172A] border border-white/5 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-[#94A3B8]">Name</TableHead>
              <TableHead className="text-[#94A3B8]">Phone</TableHead>
              <TableHead className="text-[#94A3B8]">Message</TableHead>
              <TableHead className="text-[#94A3B8]">Date</TableHead>
              <TableHead className="text-[#94A3B8]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map(c => (
              <TableRow key={c.id} className="border-white/5 hover:bg-white/5">
                <TableCell className="text-[#F8FAFC] font-['Outfit']">{c.name}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit']">{c.phone}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit'] max-w-[300px] truncate">{c.message}</TableCell>
                <TableCell className="text-[#94A3B8] font-['Outfit'] text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                  <button data-testid={`delete-contact-${c.id}`} onClick={() => deleteContact(c.id)} className="p-1.5 rounded hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {contacts.length === 0 && <p className="text-center text-[#94A3B8] py-8 font-['Outfit']">No messages yet</p>}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate('/admin/login');
  }, [user, loading, navigate]);

  const fetchAll = async () => {
  try {
    const [s, e, c] = await Promise.all([
      api.get("/spaces"),
      api.get("/enquiries"),
      api.get("/contact"),
    ]);
      setSpaces(s.data);
      setEnquiries(e.data);
      setContacts(c.data);
    } catch {}
  };

  useEffect(() => { if (user) fetchAll(); }, [user]);

  if (loading) return <div className="min-h-screen bg-[#0B1120] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0B1120] flex">
      <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileOpen(false)} />}
      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <div className="md:hidden mb-4">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} className="p-2 rounded-md bg-[#0F172A] border border-white/10 text-[#94A3B8]"><Menu className="w-5 h-5" /></button>
        </div>
        {activeTab === 'dashboard' && <DashboardView spaces={spaces} enquiries={enquiries} contacts={contacts} />}
        {activeTab === 'spaces' && <SpacesView spaces={spaces} onRefresh={fetchAll} />}
        {activeTab === 'enquiries' && <EnquiriesView enquiries={enquiries} onRefresh={fetchAll} />}
        {activeTab === 'contacts' && <ContactsView contacts={contacts} onRefresh={fetchAll} />}
      </main>
    </div>
  );
}
